/**
 * GM Race Debug Overlay Config
 *
 * Controls the race-specific debug overlay that surfaces live race state
 * during playtesting: phase + countdown, checkpoint progress, hazard/powerup
 * positions, ability gate, event log, and claim-reject telemetry.
 *
 * ON by default while the GM Race arc is in active playtesting (A.6/A.7).
 * Flip `defaultEnabled` to `false` (or set `RACE_DEBUG_DEV_OVERRIDE.defaultEnabled = false`)
 * when the arc ships and we no longer need it on every boot.
 *
 * Two-section file (the simple "defaults + dev override" pattern, see
 * aggro-overlay.config.ts). Colors are numeric hex (0xRRGGBB) to match the
 * IGraphics fill/stroke API.
 */

export interface RaceDebugOverlaySection {
  /** Whether this section is shown in the overlay panel. */
  enabled: boolean;
  /** Label color (hex) for this section's header in the panel. */
  color: number;
}

export interface RaceDebugRenderConfig {
  /** Whether this in-world render toggle is on at load. */
  defaultEnabled: boolean;
  /** Outline color (0xRRGGBB) for the AABBs. */
  color: number;
  /** Outline width (px). */
  lineWidth: number;
  /** Outline alpha (0-1). */
  alpha: number;
}

export interface RaceDebugConfig {
  /** Master switch — when false, the overlay panel + all rendering is off. */
  defaultEnabled: boolean;
  /** Show the session/phase/countdown section. */
  session: RaceDebugOverlaySection;
  /** Show the local player's race progress (checkpoint, lap, place, finish time). */
  playerState: RaceDebugOverlaySection;
  /** Show hazard positions + kinds + active state. */
  hazards: RaceDebugOverlaySection;
  /** Show powerup positions + availability + active effects. */
  powerups: RaceDebugOverlaySection;
  /** Show the ability gate (disabled list, allowlist mode). */
  abilityGate: RaceDebugOverlaySection;
  /** Show a rolling event log (recent race:* + gmEvent* events). */
  eventLog: RaceDebugOverlaySection;
  /** Max events to keep in the rolling log. */
  eventLogMaxEntries: number;
  /** Show claim-reject / desync telemetry (the M2 desync freeze debug). */
  desync: RaceDebugOverlaySection;
  /** Panel opacity (0-1). */
  panelAlpha: number;

  // ── In-world render toggles (drawn by RaceDebugRenderer) ──────────

  /** Draw AABB outlines around hazards (swing hammers, bounce pads, etc.). */
  renderHitboxes: RaceDebugRenderConfig;
  /** Draw AABB outlines around powerup pickups. */
  renderPowerupBoxes: RaceDebugRenderConfig;
  /** Draw checkpoint / start-gate / finish-line markers. */
  renderCheckpoints: RaceDebugRenderConfig;
}

export const RACE_DEBUG_DEFAULTS: RaceDebugConfig = {
  defaultEnabled: true,
  session:      { enabled: true, color: 0x3498db },
  playerState:  { enabled: true, color: 0x2ecc71 },
  hazards:      { enabled: true, color: 0xe74c3c },
  powerups:     { enabled: true, color: 0xf39c12 },
  abilityGate:  { enabled: true, color: 0x9b59b6 },
  eventLog:     { enabled: true, color: 0xaaaaaa },
  eventLogMaxEntries: 12,
  desync:       { enabled: true, color: 0xff4444 },
  panelAlpha: 0.85,

  renderHitboxes:     { defaultEnabled: true, color: 0xff4444, lineWidth: 2, alpha: 0.8 },
  renderPowerupBoxes: { defaultEnabled: true, color: 0xf39c12, lineWidth: 2, alpha: 0.8 },
  renderCheckpoints:  { defaultEnabled: true, color: 0x00ffff, lineWidth: 2, alpha: 0.6 },
};

/**
 * Per-dev-environment overrides. Partial — anything omitted falls through
 * to RACE_DEBUG_DEFAULTS. Set `{ defaultEnabled: false }` when the arc ships.
 */
export const RACE_DEBUG_DEV_OVERRIDE: Partial<RaceDebugConfig> = {};

export const RACE_DEBUG: RaceDebugConfig = {
  ...RACE_DEBUG_DEFAULTS,
  ...RACE_DEBUG_DEV_OVERRIDE,
};
